// @ts-ignore
import { emailRegex } from "cwb-react";

export { emailRegex };

export const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s-`']+$/i;

export const passwordRegex =
  /^(?=.*[a-z])(?=.*\d)[a-z\d\s!@#$%^&*+-=_<>?]{6,}$/i;

export const emailValidation = (email: string, t: any) => {
  let errors: string = "";

  //Email
  if (!email) {
    errors = t("Email is required");
    return errors;
  }

  if (!email.match(emailRegex)) errors = t("Email is not valid");

  if (isEmailOnRejectionList(email))
    errors = emailRejectionMessage(email, t, t("Email is not valid"));

  return errors;
};

// Copy for an address the screening refused. A misspelled provider is the one
// rejection that can explain itself, so name the address the user meant instead
// of dead-ending on "Email is invalid" — which reads identically to a malformed
// address and sends the user to support. `invalidMessage` is the caller's own
// wording because the registration forms are not consistent ("Email is
// invalid." vs "Email is not valid").
export const emailRejectionMessage = (
  email: string,
  t: any,
  invalidMessage: string
): string => {
  const suggestion = getSuggestedEmail(email);
  return suggestion
    ? t("Did you mean {{email}}?", { email: suggestion })
    : invalidMessage;
};

export const isValidEmail = (email: string) => {
  if (!email) return false;

  if (!email.match(emailRegex)) return false;

  if (isEmailOnRejectionList(email)) return false;

  return true;
};

// --- Email domain screening -------------------------------------------------
// Two data files back the rejection list:
//   emailDomainBlacklist.json — ~33k disposable/throwaway providers.
//   emailDomainTypos.json     — misspellings of the providers this funnel
//                               actually sees, mapped to the domain the user
//                               meant ("gmail.con" -> "gmail.com").
// Both are rejected. Only the typo list can explain itself, so the UI can
// offer "Did you mean ...?" via getSuggestedEmail() instead of a dead end.
//
// The host is normalised before lookup: a domain is case-insensitive, so
// "A@GMAIL.CON" is the same signup as "a@gmail.con" and the old
// `x == emailDomain` comparison let every capitalised variant through. It also
// splits on the LAST "@" the way a mail server does.

type DomainSet = { [domain: string]: true };

let rejectionSet: DomainSet | null = null;

// The blacklist is a 33k-entry array. `.some()` over it ran on every keystroke
// of every signup form; build the lookup once and keep it.
const getRejectionSet = (): DomainSet => {
  if (rejectionSet) return rejectionSet;
  const set: DomainSet = Object.create(null);
  const blacklist: string[] =
    require("./emailDomainBlacklist.json").emailDomainBlacklist.domains || [];
  for (let i = 0; i < blacklist.length; i++) {
    const domain = (blacklist[i] || "").trim().toLowerCase();
    if (domain) set[domain] = true;
  }
  // Every typo is also a rejected domain — one source of truth, so a domain
  // added to the typo map is blocked without touching the blacklist too.
  const typos = getTypoMap();
  for (const typo in typos) set[typo] = true;
  rejectionSet = set;
  return set;
};

let typoMap: { [typo: string]: string } | null = null;

const getTypoMap = (): { [typo: string]: string } => {
  if (typoMap) return typoMap;
  const raw: { [typo: string]: string } =
    require("./emailDomainTypos.json").emailDomainTypos.typos || {};
  const map: { [typo: string]: string } = Object.create(null);
  // Copy onto a prototype-less object: `raw` comes from JSON.parse, so
  // raw["constructor"] would answer with Function instead of undefined.
  for (const typo in raw) {
    if (Object.prototype.hasOwnProperty.call(raw, typo)) {
      const key = typo.trim().toLowerCase();
      if (key) map[key] = (raw[typo] || "").trim().toLowerCase();
    }
  }
  typoMap = map;
  return map;
};

export const getEmailDomain = (email: string): string => {
  const trimmed = (email || "").trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 0) return "";
  return trimmed.slice(at + 1).toLowerCase();
};

const isEmailOnRejectionList = (email: string) => {
  if (!email) return true;
  const emailDomain = getEmailDomain(email);
  if (!emailDomain) return true;

  return getRejectionSet()[emailDomain] === true;
};

// "" when the domain is not a known misspelling, otherwise the domain the user
// most likely meant.
export const getEmailDomainSuggestion = (email: string): string => {
  const emailDomain = getEmailDomain(email);
  if (!emailDomain) return "";
  return getTypoMap()[emailDomain] || "";
};

// "" unless the address is a known misspelling, otherwise the same local part
// on the corrected domain — ready to show as "Did you mean <this>?".
export const getSuggestedEmail = (email: string): string => {
  const suggestion = getEmailDomainSuggestion(email);
  if (!suggestion) return "";
  const trimmed = (email || "").trim();
  const localPart = trimmed.slice(0, trimmed.lastIndexOf("@"));
  if (!localPart) return "";
  return localPart + "@" + suggestion;
};

export const passwordValidation = (password: string, min: number, t: any) => {
  let errors: string = "";

  if (!password) {
    errors = t("Password is required");
  }

  if (password && password.length < min) {
    errors = t(`Minimum ${min} characters`);
  }

  if (!passwordRegex.test(password)) {
    errors = t("Must contain at least 6 characters, 1 letter and 1 number");
  }

  return errors;
};

export const phoneValidation = (phone: string, t: any) => {
  let errors: string = "";
  if (!phone) return errors;

  const regex = /^[1-9]\d{7,14}$/;

  if (!phone.match(regex)) {
    errors = t("Invalid Phone Number");
  }

  return errors;
};

export const postalCodeValidation = (postalCode: string, t: any) => {
  if (
    postalCode.length <= 10 &&
    (/^\d{5}(?:[-\s]\d{3})?$/.test(postalCode) ||
      /^[0-9]{4,7}$/.test(postalCode) ||
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode) ||
      /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/.test(
        postalCode
      ))
  ) {
    return "";
  }

  return t("Postal Code is not valid");
};
